import { type ReactNode, useEffect, useRef, useState } from 'react';
import {
  Wallet, AlertCircle, AlertTriangle, Search, Filter,
  ChevronLeft, ChevronRight, CheckCircle, XCircle,
  Eye, Loader2, Zap, PauseCircle, PlayCircle, Trophy, Coins, X, ChevronDown, Check
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useApp } from "@/contexts/AppContext";
import {
  PAYOUT_USERS, PAYOUT_GRAPH,
  KXT_WITHDRAWALS, KXT_WITHDRAWALS_APPROVED, KXT_WITHDRAWALS_REJECTED,
  KXT_WITHDRAWAL_BY_ID, KXT_APPROVE, KXT_REJECT,
  KXT_STATS, KXT_POOL, KXT_MINT, KXT_RATE_USD,
  KXT_ADMIN_LEADERBOARD,
  KXT_CONTRACT_PAUSE, KXT_CONTRACT_UNPAUSE,
  USER_USERS
} from '@/api/services/adminservice';

import { useTranslation } from 'react-i18next';
import { getErrorMessage } from "@/utils/errorUtils";

interface AdminWithdrawalUser {
  id?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  phone?: string | null;
  profileImage?: string;
  role?: string;
}

interface AdminWithdrawalRecord {
  _id?: string;
  user?: AdminWithdrawalUser;
  amount?: number;
  currency?: string;
  withdrawalMethod?: string;
  withdrawalDetails?: Record<string, string | null | undefined>;
  status?: string;
  fee?: number;
  netAmount?: number;
  metadata?: Record<string, string | null | undefined>;
  createdAt?: string;
  updatedAt?: string;
  processedBy?: string | null;
}

interface AdminWithdrawalDetailsPayload {
  withdrawal?: AdminWithdrawalRecord;
  operatorWallet?: string | null;
  operatorKXTBalance?: number | null;
  walletDetails?: Record<string, string | null | undefined>;
}

interface AdminWithdrawalPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

const WITHDRAWAL_PAGE_LIMIT = 10;
const DEFAULT_WITHDRAWAL_PAGINATION: AdminWithdrawalPagination = {
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  itemsPerPage: WITHDRAWAL_PAGE_LIMIT,
  hasNext: false,
  hasPrevious: false,
};

const AdminPayoutDashboard = () => {
  const { t, i18n } = useTranslation();
  const { theme } = useApp();
  const isDark = theme === 'dark';

  const [timeframe, setTimeframe] = useState('week');
  const [payoutdash, setPayoutDash] = useState<Record<string, unknown>>({
    totalPaidOut: { amount: 0 },
    pendingPayouts: { count: 0, amount: 0 },
    failedTransactions: { count: 0 },
  });
  const [kxtStats, setKxtStats]     = useState<Record<string, unknown>>(null);
  const [poolStatus, setPoolStatus] = useState<Record<string, unknown>>(null);
  const [contractPaused, setContractPaused] = useState(false);
  const [contractLoading, setContractLoading] = useState(false);
  const [payoutdata, setPayoutdata] = useState<unknown[]>([]);
  const [leaderboard, setLeaderboard] = useState<unknown[]>([]);
  const [lbPeriod, setLbPeriod]     = useState('weekly');
  const [lbLoading, setLbLoading]   = useState(false);
  const [showMintModal, setShowMintModal] = useState(false);
  const [mintForm, setMintForm]     = useState({ userId: '', amount: '', reason: '' });
  const [mintLoading, setMintLoading] = useState(false);
  const [mintOperatorSearch, setMintOperatorSearch] = useState('');
  const [isMintOperatorOpen, setIsMintOperatorOpen] = useState(false);
  const [operators, setOperators]   = useState<unknown[]>([]);
  const [operatorsLoading, setOperatorsLoading] = useState(false);
  const mintSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [withdrawals, setWithdrawals] = useState<AdminWithdrawalRecord[]>([]);
  const [wLoading, setWLoading]     = useState(false);
  const [approvedWithdrawals, setApprovedWithdrawals] = useState<AdminWithdrawalRecord[]>([]);
  const [approvedLoading, setApprovedLoading] = useState(false);
  const [approvedLoadingMore, setApprovedLoadingMore] = useState(false);
  const [approvedPagination, setApprovedPagination] = useState<AdminWithdrawalPagination>(DEFAULT_WITHDRAWAL_PAGINATION);
  const [rejectedWithdrawals, setRejectedWithdrawals] = useState<AdminWithdrawalRecord[]>([]);
  const [rejectedLoading, setRejectedLoading] = useState(false);
  const [rejectedLoadingMore, setRejectedLoadingMore] = useState(false);
  const [rejectedPagination, setRejectedPagination] = useState<AdminWithdrawalPagination>(DEFAULT_WITHDRAWAL_PAGINATION);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('All');
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedW, setSelectedW]   = useState<AdminWithdrawalRecord | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [detailsRequestId, setDetailsRequestId] = useState<string | null>(null);
  const [withdrawalDetails, setWithdrawalDetails] = useState<AdminWithdrawalDetailsPayload | null>(null);
  const [kxtRateAmount, setKxtRateAmount] = useState('1');
  const [usdRateAmount, setUsdRateAmount] = useState('1');
  const [kxtRateInput, setKxtRateInput] = useState('1');
  const [usdRateInput, setUsdRateInput] = useState('1');
  const [rateSaving, setRateSaving] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [startDate, setStartDate]   = useState('');
  const [endDate, setEndDate]       = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1, totalPages: 1, totalItems: 0,
    itemsPerPage: 10, hasNext: false, hasPrevious: false,
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const hasInitializedRef = useRef(false);
  const todayDate = new Date().toISOString().split('T')[0];
  const normalizeWithdrawalPagination = (value?: Partial<AdminWithdrawalPagination> | null): AdminWithdrawalPagination => ({
    currentPage: value?.currentPage ?? DEFAULT_WITHDRAWAL_PAGINATION.currentPage,
    totalPages: value?.totalPages ?? DEFAULT_WITHDRAWAL_PAGINATION.totalPages,
    totalItems: value?.totalItems ?? DEFAULT_WITHDRAWAL_PAGINATION.totalItems,
    itemsPerPage: value?.itemsPerPage ?? DEFAULT_WITHDRAWAL_PAGINATION.itemsPerPage,
    hasNext: value?.hasNext ?? DEFAULT_WITHDRAWAL_PAGINATION.hasNext,
    hasPrevious: value?.hasPrevious ?? DEFAULT_WITHDRAWAL_PAGINATION.hasPrevious,
  });
  const mergeWithdrawalRows = (
    currentRows: AdminWithdrawalRecord[],
    nextRows: AdminWithdrawalRecord[]
  ) => {
    const seen = new Set<string>();
    return [...currentRows, ...nextRows].filter((row, index) => {
      const key =
        row._id ||
        `${row.createdAt || 'no-date'}-${row.user?.email || 'no-user'}-${row.amount || 0}-${row.status || 'unknown'}-${index}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  useEffect(() => {
    const storedPair = localStorage.getItem('admin-kxt-usd-rate-pair');
    if (storedPair) {
      try {
        const parsedPair = JSON.parse(storedPair) as { kxt?: string; usd?: string };
        if (parsedPair.kxt) {
          setKxtRateAmount(parsedPair.kxt);
          setKxtRateInput(parsedPair.kxt);
        }
        if (parsedPair.usd) {
          setUsdRateAmount(parsedPair.usd);
          setUsdRateInput(parsedPair.usd);
        }
        return;
      } catch (error: unknown) {
        console.error('Failed to parse stored KXT/USD rate pair:', error);
      }
    }

    const legacyRate = localStorage.getItem('admin-kxt-usd-rate');
    if (legacyRate) {
      setKxtRateAmount('1');
      setUsdRateAmount(legacyRate);
      setKxtRateInput('1');
      setUsdRateInput(legacyRate);
    }
  }, []);

  useEffect(() => {
    i18n.addResourceBundle(
      'fr',
      'translation',
      {
        admin: {
          payoutPage: {
            messages: {
              withdrawalApproved: 'Retrait approuve avec succes',
              withdrawalRejected: 'Retrait rejete',
              contractUnpaused: 'Contrat reactive avec succes',
              contractPaused: 'Contrat mis en pause avec succes',
              operatorAmountRequired: "L'operateur et le montant sont requis",
              mintedSuccess: '{{amount}} KXT frappes avec succes'
            },
            stats: {
              totalPaidOut: 'Total des KXT verses',
              pendingWithdrawals: 'Retraits en attente',
              pendingAmount: '{{amount}} KXT en attente',
              failedTransactions: 'Transactions echouees',
              totalMinted: 'Total des KXT frappes',
              poolBalance: 'Pool : {{amount}} KXT'
            },
            contract: {
              title: 'Controles du contrat',
              currently: 'Le contrat est actuellement',
              paused: 'EN PAUSE',
              active: 'ACTIF',
              pause: 'Mettre le contrat en pause',
              unpause: 'Reactiver le contrat'
            },
            trends: {
              title: 'Tendances des paiements'
            },
            leaderboard: {
              title: 'Classement KXT',
              daily: 'Quotidien',
              weekly: 'Hebdomadaire',
              monthly: 'Mensuel',
              empty: 'Aucune donnee de classement',
              score: 'Score'
            },
            withdrawals: {
              title: 'Demandes de retrait KXT',
              searchPlaceholder: 'Rechercher par ID, operateur ou methode...',
              filter: 'Filtrer',
              empty: 'Aucune demande de retrait trouvee.'
            },
            approvedSection: {
              title: 'Retraits approuves',
              empty: 'Aucun retrait approuve trouve.',
              loadMore: 'Charger plus',
              loadingMore: 'Chargement...',
              showing: 'Affichage de {{shown}} sur {{total}}'
            },
            rejectedSection: {
              title: 'Retraits rejetes',
              empty: 'Aucun retrait rejete trouve.',
              loadMore: 'Charger plus',
              loadingMore: 'Chargement...',
              showing: 'Affichage de {{shown}} sur {{total}}'
            },
            status: {
              processing: 'En cours',
              completed: 'Termine',
              rejected: 'Rejete'
            },
            filterByDate: {
              title: 'Filtrer par date',
              start: 'Date de debut',
              end: 'Date de fin'
            },
            reject: {
              title: 'Rejeter le retrait',
              reason: 'Raison (optionnelle)',
              reasonPlaceholder: 'Entrez la raison du rejet...',
              button: 'Rejeter'
            },
            mint: {
              title: "Frapper des KXT pour l'operateur",
              selectOperator: 'Sélectionnez un opérateur ou une organisation',
              selectOperatorPlaceholder: '-- Sélectionnez un opérateur/une organisation --',
              amount: 'Montant (KXT)',
              amountPlaceholder: 'ex. 100',
              reason: 'Raison (optionnelle)',
              reasonPlaceholder: 'ex. Prime de bonus',
              button: 'Frapper KXT'
            },
            table: {
              withdrawalId: 'ID du retrait',
              operator: 'Operateur',
              amount: 'Montant',
              method: 'Methode',
              date: 'Date',
              status: 'Statut',
              actions: 'Actions'
            },
            common: {
              close: 'Fermer',
              clear: 'Effacer',
              apply: 'Appliquer',
              cancel: 'Annuler',
              approx: 'Env.',
              fee: 'Frais:',
              zeroOfZero: '0 sur 0',
              paginationRange: '{{start}} à {{end}} sur {{total}}'
            },
            details: {
              title: 'Details du retrait',
              loadError: 'Impossible de charger les details du retrait',
              sections: {
                operator: 'Operateur',
                overview: "Vue d'ensemble",
                withdrawalDestination: 'Destination du retrait',
                wallets: 'Contexte des portefeuilles',
                metadata: 'Metadonnees'
              },
              empty: {
                generic: "Aucun detail n'a ete retourne pour ce retrait.",
                destination: "Aucun detail de destination n'a ete retourne.",
                metadata: "Aucune metadonnee supplementaire n'a ete retournee pour ce retrait."
              }
            },
            rate: {
              title: 'Taux KXT vers USD',
              kxtLabel: 'Montant KXT',
              usdLabel: 'Montant USD',
              helper: 'Utilise ce taux pour les apercus USD sur cette page.',
              submit: 'Enregistrer le taux',
              invalid: 'Saisis un montant KXT superieur a zero et un montant USD valide.',
              saved: 'Taux KXT/USD enregistre avec succes.',
              saveError: "Impossible d'enregistrer le taux KXT/USD.",
              preview: 'Apercu',
              perToken: '1 KXT = {{amount}}',
              hundredTokens: '100 KXT = {{amount}}'
            }
          }
        }
      },
      true,
      true
    );
  }, [i18n]);

  function getPagination(cur: number, total: number) {
    const range = (s: number, e: number) => Array.from({ length: e - s + 1 }, (_, i) => s + i);
    if (total <= 9) return range(1, total);
    const pages: (number | string)[] = [...range(1, 4)];
    if (cur > 5) pages.push('...');
    pages.push(...range(Math.max(5, cur - 1), Math.min(total - 4, cur + 1)));
    if (cur < total - 4) pages.push('...');
    pages.push(...range(total - 3, total));
    return [...new Set(pages)];
  }

  const flash = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const isValidDate = (d: unknown): d is string | number | Date => {
    if (!d) return false;
    const date = new Date(d as string | number | Date);
    return !isNaN(date.getTime());
  };

  const fmtDate = (d?: string) => {
    if (!d) return 'N/A';
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const fmtDateTime = (d?: string) => {
    if (!d) return 'N/A';
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    return date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };
  const fmtMethod = (m: string) => m ? m.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'N/A';
  const fmtAmount = (value?: number | null, currency = 'KXT') => value == null ? 'N/A' : `${Number(value).toLocaleString('en-US', { maximumFractionDigits: 4 })} ${currency}`;
  const getWithdrawalCurrency = (withdrawal?: Pick<AdminWithdrawalRecord, 'currency'> | null) => withdrawal?.currency?.trim() || 'KXT';
  const parsedKxtRateAmount = Number(kxtRateAmount);
  const parsedUsdRateAmount = Number(usdRateAmount);
  const parsedKxtRateInput = Number(kxtRateInput);
  const parsedUsdRateInput = Number(usdRateInput);
  const activeKxtUsdRate =
    Number.isFinite(parsedKxtRateAmount) &&
    parsedKxtRateAmount > 0 &&
    Number.isFinite(parsedUsdRateAmount) &&
    parsedUsdRateAmount >= 0
      ? parsedUsdRateAmount / parsedKxtRateAmount
      : 0;
  const fmtUsdFromKxt = (value?: number | null) => value == null || activeKxtUsdRate <= 0
    ? 'N/A'
    : `$${(value * activeKxtUsdRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtKxtWithUsd = (value?: number | null, currency = 'KXT') => {
    const amountText = fmtAmount(value, currency);
    if (value == null || currency !== 'KXT' || activeKxtUsdRate <= 0) return amountText;
    return `${amountText} (approx. ${fmtUsdFromKxt(value)})`;
  };
  const previewKxtUsdRate =
    Number.isFinite(parsedKxtRateInput) &&
    parsedKxtRateInput > 0 &&
    Number.isFinite(parsedUsdRateInput) &&
    parsedUsdRateInput >= 0
      ? parsedUsdRateInput / parsedKxtRateInput
      : 0;
  const fmtUsdFromDraftKxt = (value: number) => previewKxtUsdRate > 0
    ? `$${(value * previewKxtUsdRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '$0.00';
  const rateFormDirty = kxtRateInput !== kxtRateAmount || usdRateInput !== usdRateAmount;
  // Humanize a raw backend field name, but prefer a translated label when we have
  // one (so FR shows French). Unknown keys fall back to the humanized English.
  const humanizeKey = (key: string) => {
    const humanized = key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim();
    return t(`admin.payoutPage.details.fieldLabels.${key}`, { defaultValue: humanized });
  };
  const shortId = (id?: string) => id ? `#${id.slice(-8).toUpperCase()}` : 'N/A';
  const isMonospaceValue = (key: string) => /wallet|address|account|spender|id/i.test(key);
  const displayValue = (value: unknown) => value == null || value === '' ? 'N/A' : String(value);

  const statusBadge = (s: string) => {
    const l = s?.toLowerCase();
    if (l === 'completed') return isDark ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800';
    if (l === 'pending')   return isDark ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
    if (l === 'processing')return isDark ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800';
    if (l === 'rejected')  return isDark ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800';
    return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700';
  };

  const fetchPayoutTrends = async (period: string) => {
    try { const r = await PAYOUT_GRAPH({ period }); setPayoutdata(((r as unknown as Record<string, unknown>).data as Record<string, unknown>)?.data as unknown as Record<string, unknown>[] || []); }
    catch (e: unknown) { console.error(e); }
  };

  const fetchPayoutUsers = async () => {
    try { const r = await PAYOUT_USERS(); setPayoutDash(r.data as unknown as Record<string, unknown>); }
    catch (e: unknown) { console.error(e); }
  };

  const fetchKxtStats = async () => {
    try {
      const r = await KXT_STATS();
      setKxtStats(r.data as unknown as Record<string, unknown>);
      setContractPaused(((r.data as unknown as Record<string, unknown>)?.contractPaused as boolean) ?? false);
    } catch (e: unknown) { console.error(e); }
  };

  const fetchPool = async () => {
    try { const r = await KXT_POOL(); setPoolStatus(r.data as unknown as Record<string, unknown>); }
    catch (e: unknown) { console.error(e); }
  };

  const fetchLeaderboard = async (period: string) => {
    try {
      setLbLoading(true);
      const r = await KXT_ADMIN_LEADERBOARD(period);
      setLeaderboard(((r.data as unknown as Record<string, unknown>)?.leaderboard as unknown as Record<string, unknown>[]) || ((r.data as unknown as Record<string, unknown>)?.operators as unknown as Record<string, unknown>[]) || []);
    } catch (e: unknown) { console.error(e); }
    finally { setLbLoading(false); }
  };

  const fetchOperators = async (query = '') => {
    try {
      setOperatorsLoading(true);
      const params: Record<string, unknown> = { page: 1, limit: 100, role: 'operator,organisation' };
      if (query.trim()) params.q = query.trim();
      const r = await USER_USERS(params);
      const normalizedOperators = (((r.data as unknown as Record<string, unknown>)?.users as unknown as Record<string, unknown>[]) || [])
        .map((operator: Record<string, unknown>) => ({
          ...operator,
          operatorId: getOperatorId(operator),
          operatorLabel: getOperatorLabel(operator),
        }))
        .filter((operator: Record<string, unknown>) => Boolean(operator.operatorId));
      setOperators(normalizedOperators);
    } catch (e: unknown) { console.error(e); }
    finally { setOperatorsLoading(false); }
  };

  const fetchWithdrawals = async (page = 1, status?: string) => {
    try {
      setWLoading(true);
      const s = status ?? statusFilter;
      const r = await KXT_WITHDRAWALS({ page, limit: WITHDRAWAL_PAGE_LIMIT, status: s === 'All' ? undefined : s.toLowerCase() });
      setWithdrawals(r.data?.withdrawals || []);
      setPagination(normalizeWithdrawalPagination(r.data?.pagination));
    } catch (e: unknown) { console.error(e); }
    finally { setWLoading(false); }
  };

  const fetchApprovedWithdrawals = async (page = 1, append = false) => {
    try {
      if (append) {
        setApprovedLoadingMore(true);
      } else {
        setApprovedLoading(true);
      }
      const r = await KXT_WITHDRAWALS_APPROVED({ page, limit: WITHDRAWAL_PAGE_LIMIT });
      const nextRows = r.data?.withdrawals || [];
      setApprovedWithdrawals((currentRows) => append ? mergeWithdrawalRows(currentRows, nextRows) : nextRows);
      setApprovedPagination(normalizeWithdrawalPagination(r.data?.pagination));
    } catch (e: unknown) { console.error(e); }
    finally {
      if (append) {
        setApprovedLoadingMore(false);
      } else {
        setApprovedLoading(false);
      }
    }
  };

  const refreshApprovedWithdrawals = async () => {
    const pagesToLoad = Math.max(approvedPagination.currentPage, 1);
    try {
      setApprovedLoading(true);
      const responses = await Promise.all(
        Array.from({ length: pagesToLoad }, (_, index) =>
          KXT_WITHDRAWALS_APPROVED({ page: index + 1, limit: WITHDRAWAL_PAGE_LIMIT })
        )
      );
      const nextRows = responses.flatMap((response) => response.data?.withdrawals || []);
      const latestPagination = responses[responses.length - 1]?.data?.pagination;
      setApprovedWithdrawals(mergeWithdrawalRows([], nextRows));
      setApprovedPagination(normalizeWithdrawalPagination(latestPagination));
    } catch (e: unknown) {
      console.error(e);
    } finally {
      setApprovedLoading(false);
      setApprovedLoadingMore(false);
    }
  };

  const fetchRejectedWithdrawals = async (page = 1, append = false) => {
    try {
      if (append) {
        setRejectedLoadingMore(true);
      } else {
        setRejectedLoading(true);
      }
      const r = await KXT_WITHDRAWALS_REJECTED({ page, limit: WITHDRAWAL_PAGE_LIMIT });
      const nextRows = r.data?.withdrawals || [];
      setRejectedWithdrawals((currentRows) => append ? mergeWithdrawalRows(currentRows, nextRows) : nextRows);
      setRejectedPagination(normalizeWithdrawalPagination(r.data?.pagination));
    } catch (e: unknown) { console.error(e); }
    finally {
      if (append) {
        setRejectedLoadingMore(false);
      } else {
        setRejectedLoading(false);
      }
    }
  };

  const refreshRejectedWithdrawals = async () => {
    const pagesToLoad = Math.max(rejectedPagination.currentPage, 1);
    try {
      setRejectedLoading(true);
      const responses = await Promise.all(
        Array.from({ length: pagesToLoad }, (_, index) =>
          KXT_WITHDRAWALS_REJECTED({ page: index + 1, limit: WITHDRAWAL_PAGE_LIMIT })
        )
      );
      const nextRows = responses.flatMap((response) => response.data?.withdrawals || []);
      const latestPagination = responses[responses.length - 1]?.data?.pagination;
      setRejectedWithdrawals(mergeWithdrawalRows([], nextRows));
      setRejectedPagination(normalizeWithdrawalPagination(latestPagination));
    } catch (e: unknown) {
      console.error(e);
    } finally {
      setRejectedLoading(false);
      setRejectedLoadingMore(false);
    }
  };

  const loadMoreApprovedWithdrawals = () => {
    if (approvedLoading || approvedLoadingMore || !approvedPagination.hasNext) return;
    void fetchApprovedWithdrawals(approvedPagination.currentPage + 1, true);
  };

  const loadMoreRejectedWithdrawals = () => {
    if (rejectedLoading || rejectedLoadingMore || !rejectedPagination.hasNext) return;
    void fetchRejectedWithdrawals(rejectedPagination.currentPage + 1, true);
  };

  const handleApprove = async (withdrawal: AdminWithdrawalRecord) => {
    if (!withdrawal._id) return;
    try {
      setActionLoading(withdrawal._id);
      await KXT_APPROVE(
        withdrawal._id,
        withdrawal.withdrawalMethod === 'bank_transfer' ? { status: 'approved' } : undefined
      );
      flash('success', t('admin.payoutPage.messages.withdrawalApproved', { defaultValue: 'Withdrawal approved successfully' }));
      await Promise.all([
        fetchWithdrawals(pagination.currentPage),
        fetchPayoutUsers(),
        refreshApprovedWithdrawals(),
        refreshRejectedWithdrawals(),
      ]);
    } catch (e: unknown) { flash('error', ((e as unknown as Record<string, unknown>).message as string) || 'Failed to approve'); }
    finally { setActionLoading(null); }
  };

  const handleRejectConfirm = async () => {
    if (!selectedW?._id) return;
    try {
      setActionLoading(selectedW._id);
      await KXT_REJECT(selectedW._id, rejectReason);
      flash('success', t('admin.payoutPage.messages.withdrawalRejected', { defaultValue: 'Withdrawal rejected' }));
      setShowRejectModal(false); setSelectedW(null); setRejectReason('');
      await Promise.all([
        fetchWithdrawals(pagination.currentPage),
        fetchPayoutUsers(),
        refreshApprovedWithdrawals(),
        refreshRejectedWithdrawals(),
      ]);
    } catch (e: unknown) { flash('error', ((e as unknown as Record<string, unknown>).message as string) || 'Failed to reject'); }
    finally { setActionLoading(null); }
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setDetailsError(null);
    setDetailsRequestId(null);
    setWithdrawalDetails(null);
  };

  const handleViewWithdrawal = async (id: string) => {
    try {
      setShowDetailsModal(true);
      setDetailsLoading(true);
      setDetailsError(null);
      setDetailsRequestId(id);
      setWithdrawalDetails(null);
      const response = await KXT_WITHDRAWAL_BY_ID(id);
      setWithdrawalDetails(response.data || null);
    } catch (e: unknown) {
      setDetailsError(((e as unknown as Record<string, unknown>).message as string) || t('admin.payoutPage.details.loadError', { defaultValue: 'Failed to load withdrawal details' }));
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleSubmitRate = async () => {
    const parsedKxtInput = Number(kxtRateInput);
    const parsedUsdInput = Number(usdRateInput);

    if (!Number.isFinite(parsedKxtInput) || parsedKxtInput <= 0 || !Number.isFinite(parsedUsdInput) || parsedUsdInput < 0) {
      flash('error', t('admin.payoutPage.rate.invalid', { defaultValue: 'Enter a KXT amount greater than zero and a valid USD amount.' }));
      return;
    }

    try {
      setRateSaving(true);
      await KXT_RATE_USD({
        kxt: parsedKxtInput,
        usd: parsedUsdInput,
      });

      const normalizedKxt = String(parsedKxtInput);
      const normalizedUsd = String(parsedUsdInput);

      setKxtRateAmount(normalizedKxt);
      setUsdRateAmount(normalizedUsd);
      localStorage.setItem('admin-kxt-usd-rate-pair', JSON.stringify({
        kxt: normalizedKxt,
        usd: normalizedUsd,
      }));
      flash('success', t('admin.payoutPage.rate.saved', { defaultValue: 'KXT/USD rate saved successfully.' }));
    } catch (e: unknown) {
      flash('error', ((e as unknown as Record<string, unknown>).message as string) || t('admin.payoutPage.rate.saveError', { defaultValue: 'Failed to save KXT/USD rate.' }));
    } finally {
      setRateSaving(false);
    }
  };

  const handleToggleContract = async () => {
    try {
      setContractLoading(true);
      if (contractPaused) {
        await KXT_CONTRACT_UNPAUSE();
        flash('success', t('admin.payoutPage.messages.contractUnpaused', { defaultValue: 'Contract unpaused successfully' }));
        setContractPaused(false);
      } else {
        await KXT_CONTRACT_PAUSE();
        flash('success', t('admin.payoutPage.messages.contractPaused', { defaultValue: 'Contract paused successfully' }));
        setContractPaused(true);
      }
    } catch (e: unknown) { flash('error', ((e as unknown as Record<string, unknown>).message as string) || 'Contract action failed'); }
    finally { setContractLoading(false); }
  };

  const handleMint = async () => {
    if (!mintForm.userId || !mintForm.amount) { flash('error', t('admin.payoutPage.messages.operatorAmountRequired', { defaultValue: 'Operator and amount are required' })); return; }
    try {
      setMintLoading(true);
      await KXT_MINT({ operatorId: mintForm.userId, amount: Number(mintForm.amount), reason: mintForm.reason });
      flash('success', t('admin.payoutPage.messages.mintedSuccess', { defaultValue: 'Minted {{amount}} KXT successfully', amount: mintForm.amount }));
      setShowMintModal(false);
      setMintForm({ userId: '', amount: '', reason: '' });
      setMintOperatorSearch('');
      setIsMintOperatorOpen(false);
      await fetchKxtStats();
    } catch (e: unknown) { flash('error', ((e as unknown as Record<string, unknown>).message as string) || 'Failed to mint KXT'); }
    finally { setMintLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        if (cancelled) return;

        await Promise.allSettled([
          fetchPayoutUsers(),
          fetchKxtStats(),
          fetchPool(),
          fetchPayoutTrends(timeframe),
          fetchWithdrawals(1),
          fetchApprovedWithdrawals(1),
          fetchRejectedWithdrawals(1),
          fetchLeaderboard(lbPeriod),
          fetchOperators(),
        ]);
      } finally {
        if (!cancelled) {
          hasInitializedRef.current = true;
        }
      }
    }

    void init();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!hasInitializedRef.current) return;
    void fetchPayoutTrends(timeframe);
  }, [timeframe]);

  useEffect(() => {
    if (!hasInitializedRef.current) return;
    void fetchLeaderboard(lbPeriod);
  }, [lbPeriod]);

  useEffect(() => {
    if (mintSearchTimerRef.current) clearTimeout(mintSearchTimerRef.current);
    mintSearchTimerRef.current = setTimeout(() => {
      void fetchOperators(mintOperatorSearch);
    }, 300);
    return () => {
      if (mintSearchTimerRef.current) clearTimeout(mintSearchTimerRef.current);
    };
  }, [mintOperatorSearch]);

  const filtered = withdrawals.filter(w => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return w._id?.toLowerCase().includes(q) || w.withdrawalMethod?.toLowerCase().includes(q) || w.status?.toLowerCase().includes(q) ||
      w.user?.email?.toLowerCase().includes(q) || `${w.user?.firstName} ${w.user?.lastName}`.toLowerCase().includes(q);
  });

  const card = `${isDark ? 'bg-[#333436] border-gray-700' : 'bg-white border-gray-200'} rounded-lg border shadow-sm`;
  const txt  = isDark ? 'text-white' : 'text-gray-900';
  const sub  = isDark ? 'text-gray-400' : 'text-gray-600';
  const inp  = `w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-[#0f1011] border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'}`;
  const detailCard = `${isDark ? 'bg-[#0f1011] border-gray-700' : 'bg-gray-50 border-gray-200'} rounded-xl border p-4`;
  const getOperatorId = (operator: Record<string, unknown>) => operator?.id || operator?._id || operator?.operatorId || operator?.userId || '';
  const getOperatorLabel = (operator: Record<string, unknown>) => {
    const fullName = [operator?.firstName, operator?.lastName].filter(Boolean).join(' ').trim();
    return fullName || operator?.email || operator?.name || operator?.userId || 'Operator';
  };
  const detailsWithdrawal = withdrawalDetails?.withdrawal;
  const detailsUser = detailsWithdrawal?.user;
  const detailsUserName = detailsUser?.fullName || `${detailsUser?.firstName || ''} ${detailsUser?.lastName || ''}`.trim();
  const detailsUserInitial = (detailsUserName || detailsUser?.email || 'U').trim().charAt(0).toUpperCase();
  const walletDetailsEntries = Object.entries(withdrawalDetails?.walletDetails || {});
  const withdrawalDetailsEntries = Object.entries(detailsWithdrawal?.withdrawalDetails || {});
  const metadataEntries = Object.entries(detailsWithdrawal?.metadata || {}).filter(
    ([key]) => {
      const k = key.toLowerCase();
      // Filter out technical fields related to the source endpoint
      if (k.includes('createdfrom') || k.includes('created_from') || k.includes('createdatendpoint') || k.includes('created_at_endpoint')) return false;
      if (k.includes('from') && k.includes('endpoint')) return false;
      return true;
    }
  );

  const InfoSection = ({ title, children, className = '' }: { title: string; children: ReactNode; className?: string }) => (
    <section className={`${detailCard} ${className}`}>
      <h3 className={`text-sm font-semibold ${txt}`}>{title}</h3>
      <div className="mt-4 space-y-3">{children}</div>
    </section>
  );

  const InfoField = ({
    label,
    value,
    mono = false,
  }: {
    label: string;
    value: unknown;
    mono?: boolean;
  }) => (
    <div>
      <p className={`text-xs uppercase tracking-wide ${sub}`}>{label}</p>
      <p className={`mt-1 break-all text-sm ${mono ? 'font-mono' : ''} ${txt}`}>{displayValue(value)}</p>
    </div>
  );

  const WithdrawalHistorySection = ({
    title,
    rows,
    loading,
    loadingMore,
    emptyText,
    icon,
    accentClass,
    actionType,
    paginationState,
    onLoadMore,
    loadMoreLabel,
    loadingMoreLabel,
    showingLabel,
  }: {
    title: string;
    rows: AdminWithdrawalRecord[];
    loading: boolean;
    loadingMore: boolean;
    emptyText: string;
    icon: ReactNode;
    accentClass: string;
    actionType: 'approve' | 'reject';
    paginationState: AdminWithdrawalPagination;
    onLoadMore: () => void;
    loadMoreLabel: string;
    loadingMoreLabel: string;
    showingLabel: string;
  }) => (
    <div className={card}>
      <div className={`flex items-center justify-between gap-3 border-b p-4 sm:p-5 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center gap-3">
          <div className={`rounded-lg p-2 ${accentClass}`}>
            {icon}
          </div>
          <div>
            <h3 className={`text-base font-semibold ${txt}`}>{title}</h3>
          </div>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${accentClass}`}>{rows.length}</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead className={`${isDark ? 'bg-[#1a1b1d] border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
            <tr>
              {[
                t('admin.payoutPage.table.withdrawalId', { defaultValue: 'Withdrawal ID' }),
                t('admin.payoutPage.table.operator', { defaultValue: 'Operator' }),
                t('admin.payoutPage.table.amount', { defaultValue: 'Amount' }),
                t('admin.payoutPage.table.method', { defaultValue: 'Method' }),
                t('admin.payoutPage.table.date', { defaultValue: 'Date' }),
                t('admin.payoutPage.table.status', { defaultValue: 'Status' }),
                t('admin.payoutPage.table.actions', { defaultValue: 'Actions' }),
              ].map(h => (
                <th key={h} className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${sub}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className={`divide-y ${isDark ? 'bg-[#333436] divide-gray-700' : 'bg-white divide-gray-200'}`}>
            {loading && (
              <tr>
                <td colSpan={7} className={`px-4 py-8 text-center ${sub}`}>
                  <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className={`px-4 py-8 text-center ${sub}`}>{emptyText}</td>
              </tr>
            )}
            {!loading && rows.map(w => {
              const rowCurrency = getWithdrawalCurrency(w);

              return (
                <tr key={w._id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`text-xs font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{shortId(w._id)}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <p className={`text-sm font-medium ${txt}`}>{w.user?.firstName} {w.user?.lastName}</p>
                    <p className={`text-xs ${sub}`}>{w.user?.email}</p>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <p className={`text-sm font-bold ${txt}`}>{fmtAmount(w.amount, rowCurrency)}</p>
                    {rowCurrency === 'KXT' && activeKxtUsdRate > 0 && <p className={`text-xs ${sub}`}>{t('admin.payoutPage.common.approx', { defaultValue: 'Approx.' })} {fmtUsdFromKxt(w.amount)}</p>}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{fmtMethod(w.withdrawalMethod || '')}</p>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{fmtDate(w.createdAt)}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusBadge(w.status || '')}`}>{fmtMethod(w.status || '')}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {actionType === 'approve' ? (
                        <button
                          onClick={() => handleApprove(w)}
                          disabled={!w._id || !!actionLoading}
                         
                          className="rounded-lg bg-green-600 p-1.5 text-white hover:bg-green-700 disabled:opacity-50"
                        >
                          {actionLoading === w._id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
                        </button>
                      ) : (
                        <button
                          onClick={() => { setSelectedW(w); setShowRejectModal(true); }}
                          disabled={!!actionLoading}
                         
                          className="rounded-lg bg-red-600 p-1.5 text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          <XCircle className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => w._id && handleViewWithdrawal(w._id)}
                       
                        disabled={!w._id || (detailsLoading && detailsRequestId === w._id)}
                        className={`rounded-lg p-1.5 disabled:opacity-50 ${isDark ? 'text-gray-400 hover:bg-gray-600' : 'text-gray-500 hover:bg-gray-100'}`}
                      >
                        {detailsLoading && detailsRequestId === w._id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!loading && rows.length > 0 && (
        <div className={`flex flex-col gap-3 border-t px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <p className={`text-sm ${sub}`}>
            {showingLabel}
          </p>
          {paginationState.hasNext && (
            <button
              type="button"
              onClick={onLoadMore}
              disabled={loadingMore}
              className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60 ${isDark ? 'border border-gray-600 text-gray-100 hover:bg-gray-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >
              {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              <span>{loadingMore ? loadingMoreLabel : loadMoreLabel}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className={`min-h-screen ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">

        {message && (
          <div className={`p-4 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'}`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
            <span className="text-sm">{message.text}</span>
          </div>
        )}

        {/* 4 stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: t('admin.payoutPage.stats.totalPaidOut', { defaultValue: 'Total KXT Paid Out' }), value: `${((payoutdash as unknown as Record<string, unknown>).totalPaidOut as Record<string, unknown>)?.amount ?? 0} KXT`, icon: <Wallet className="w-5 h-5" />, bg: isDark ? 'bg-gray-700' : 'bg-gray-100', ic: isDark ? 'text-gray-300' : 'text-gray-700' },
            { label: t('admin.payoutPage.stats.pendingWithdrawals', { defaultValue: 'Pending Withdrawals' }), value: String(((payoutdash as unknown as Record<string, unknown>).pendingPayouts as Record<string, unknown>)?.count ?? 0), sub2: t('admin.payoutPage.stats.pendingAmount', { defaultValue: '{{amount}} KXT pending', amount: ((payoutdash as unknown as Record<string, unknown>).pendingPayouts as Record<string, unknown>)?.amount ?? 0 }), icon: <AlertCircle className="w-5 h-5" />, bg: isDark ? 'bg-yellow-900' : 'bg-yellow-100', ic: isDark ? 'text-yellow-300' : 'text-yellow-600' },
            { label: t('admin.payoutPage.stats.failedTransactions', { defaultValue: 'Failed Transactions' }), value: String(((payoutdash as unknown as Record<string, unknown>).failedTransactions as Record<string, unknown>)?.count ?? 0), icon: <AlertTriangle className="w-5 h-5" />, bg: isDark ? 'bg-red-900' : 'bg-red-100', ic: isDark ? 'text-red-300' : 'text-red-600' },
            { label: t('admin.payoutPage.stats.totalMinted', { defaultValue: 'Total KXT Minted' }), value: `${kxtStats?.totalMinted ?? 0} KXT`, sub2: t('admin.payoutPage.stats.poolBalance', { defaultValue: 'Pool: {{amount}} KXT', amount: (poolStatus as unknown as Record<string, unknown>)?.poolBalance ?? (kxtStats as unknown as Record<string, unknown>)?.poolBalance ?? 0 }), icon: <Coins className="w-5 h-5" />, bg: isDark ? 'bg-blue-900' : 'bg-blue-100', ic: isDark ? 'text-blue-300' : 'text-blue-600' },
          ].map((c, i) => (
            <div key={i} className={`${card} p-5`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-sm ${sub} mb-1`}>{c.label}</p>
                  <p className={`text-2xl font-bold ${txt}`}>{c.value}</p>
                  {c.sub2 && <p className={`text-xs mt-1 ${sub}`}>{c.sub2}</p>}
                </div>
                <div className={`${c.bg} p-2 rounded-lg`}><span className={c.ic}>{c.icon}</span></div>
              </div>
            </div>
          ))}
        </div>

        {/* Contract controls */}
        <div className={`${card} p-5 overflow-hidden`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 min-w-0">
            <div className="min-w-0">
              <h3 className={`text-base font-semibold ${txt} mb-1`}>{t('admin.payoutPage.contract.title', { defaultValue: 'Contract Controls' })}</h3>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${contractPaused ? 'bg-red-500' : 'bg-green-500'}`} />
                <span className={`text-sm ${sub}`}>
                  {t('admin.payoutPage.contract.currently', { defaultValue: 'Contract is currently' })} <span className={`font-medium ${contractPaused ? 'text-red-400' : 'text-green-400'}`}>{contractPaused ? t('admin.payoutPage.contract.paused', { defaultValue: 'PAUSED' }) : t('admin.payoutPage.contract.active', { defaultValue: 'ACTIVE' })}</span>
                </span>
              </div>
              {(kxtStats as unknown as Record<string, unknown>)?.contractAddress && <p className={`text-xs font-mono mt-1 truncate ${sub}`}>{String((kxtStats as unknown as Record<string, unknown>).contractAddress)}</p>}
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto min-w-0 flex-shrink-0 sm:flex-shrink">
              <button onClick={handleToggleContract} disabled={contractLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 min-w-0 flex-1 sm:flex-initial ${contractPaused ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}`}>
                {contractLoading ? <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" /> : contractPaused ? <PlayCircle className="w-4 h-4 flex-shrink-0" /> : <PauseCircle className="w-4 h-4 flex-shrink-0" />}
                <span className="line-clamp-1">{contractPaused ? t('admin.payoutPage.contract.unpause', { defaultValue: 'Unpause Contract' }) : t('admin.payoutPage.contract.pause', { defaultValue: 'Pause Contract' })}</span>
              </button>
              <button onClick={() => setShowMintModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white min-w-0 flex-1 sm:flex-initial">
                <Zap className="w-4 h-4 flex-shrink-0" /> <span className="line-clamp-1">{t('admin.payoutPage.mint.button', { defaultValue: 'Mint KXT' })}</span>
              </button>
            </div>
          </div>
        </div>

        <div className={`${card} p-5`}>
          <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,560px)] xl:items-start">
            <div className="max-w-xl">
              <h3 className={`text-base font-semibold ${txt}`}>{t('admin.payoutPage.rate.title', { defaultValue: 'KXT to USD Rate' })}</h3>
              <p className={`mt-1 text-sm ${sub}`}>{t('admin.payoutPage.rate.helper', { defaultValue: 'Use this rate for USD previews on this page.' })}</p>
            </div>
            <div className={`rounded-2xl border p-4 ${isDark ? 'border-gray-700 bg-[#0f1011]' : 'border-gray-200 bg-gray-50'}`}>
              <div className="grid gap-4 lg:grid-cols-2">
                <div>
                  <label className={`mb-1 block text-sm ${sub}`}>{t('admin.payoutPage.rate.kxtLabel', { defaultValue: 'KXT Amount' })}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={kxtRateInput}
                    onChange={e => setKxtRateInput(e.target.value)}
                    placeholder="e.g. 1"
                    className={inp}
                  />
                </div>
                <div>
                  <label className={`mb-1 block text-sm ${sub}`}>{t('admin.payoutPage.rate.usdLabel', { defaultValue: 'USD Amount' })}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={usdRateInput}
                    onChange={e => setUsdRateInput(e.target.value)}
                    placeholder="e.g. 1.25"
                    className={inp}
                  />
                </div>
              </div>
              <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className={`grid gap-1 rounded-xl border px-4 py-3 text-sm ${isDark ? 'border-gray-700 bg-[#1a1b1d]' : 'border-white bg-white'}`}>
                  <span className={`text-xs uppercase tracking-wide ${sub}`}>{t('admin.payoutPage.rate.preview', { defaultValue: 'Preview' })}</span>
                  <span className={`font-medium ${txt}`}>{t('admin.payoutPage.rate.perToken', { defaultValue: '1 KXT = {{amount}}', amount: fmtUsdFromDraftKxt(1) })}</span>
                  <span className={`font-medium ${txt}`}>{t('admin.payoutPage.rate.hundredTokens', { defaultValue: '100 KXT = {{amount}}', amount: fmtUsdFromDraftKxt(100) })}</span>
                </div>
                <button
                  type="button"
                  onClick={handleSubmitRate}
                  disabled={!rateFormDirty || rateSaving}
                  className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${isDark ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'}`}
                >
                  {rateSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  <span>{t('admin.payoutPage.rate.submit', { defaultValue: 'Save Rate' })}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Trends + Leaderboard */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className={`${card} p-5 lg:col-span-2`}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4">
              <h2 className={`text-lg font-bold ${txt} mb-2 sm:mb-0`}>{t('admin.payoutPage.trends.title', { defaultValue: 'Payout Trends' })}</h2>
              <div className={`flex ${isDark ? 'bg-[#1a1b1d]' : 'bg-gray-100'} rounded-lg p-1`}>
                {['day', 'week', 'month'].map(tab => (
                  <button key={tab} onClick={() => setTimeframe(tab)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md capitalize ${timeframe === tab ? isDark ? 'bg-white text-black' : 'bg-black text-white' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600'}`}>
                    {tab === 'day'
                      ? t('admin.common.day', { defaultValue: 'Day' })
                      : tab === 'week'
                        ? t('admin.common.week', { defaultValue: 'Week' })
                        : t('admin.common.month', { defaultValue: 'Month' })}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={payoutdata}>
                  <defs>
                    <linearGradient id="ca" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#4a4a4c' : '#f0f0f0'} />
                  <XAxis dataKey="label" tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 11 }} axisLine={{ stroke: isDark ? '#4a4a4c' : '#e5e7eb' }} />
                  <YAxis tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 11 }} axisLine={{ stroke: isDark ? '#4a4a4c' : '#e5e7eb' }} tickFormatter={v => `${Number(v) / 1000}k`} />
                  <Tooltip contentStyle={{ backgroundColor: isDark ? '#1a1b1d' : 'white', border: `1px solid ${isDark ? '#4a4a4c' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#000' }} formatter={(v: unknown) => [`${Number(v).toLocaleString()} KXT`, 'Amount']} />
                  <Area type="monotone" dataKey="amount" stroke="#6366f1" strokeWidth={2} fillOpacity={1} fill="url(#ca)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={`${card} p-5`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-base font-bold ${txt} flex items-center gap-2`}><Trophy className="w-4 h-4 text-yellow-500" /> {t('admin.payoutPage.leaderboard.title', { defaultValue: 'KXT Leaderboard' })}</h2>
              <select value={lbPeriod} onChange={e => setLbPeriod(e.target.value)}
                className={`text-xs rounded-lg px-2 py-1 border ${isDark ? 'bg-[#1a1b1d] border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                <option value="daily">{t('admin.payoutPage.leaderboard.daily', { defaultValue: 'Daily' })}</option>
                <option value="weekly">{t('admin.payoutPage.leaderboard.weekly', { defaultValue: 'Weekly' })}</option>
                <option value="monthly">{t('admin.payoutPage.leaderboard.monthly', { defaultValue: 'Monthly' })}</option>
              </select>
            </div>
            {lbLoading
              ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
              : leaderboard.length === 0
                ? <p className={`text-sm text-center py-8 ${sub}`}>{t('admin.payoutPage.leaderboard.empty', { defaultValue: 'No leaderboard data' })}</p>
                : <div className="space-y-2 max-h-52 overflow-y-auto">
                    {leaderboard.map((op, i) => (
                      <div key={i} className={`flex items-center justify-between p-2 rounded-lg ${isDark ? 'bg-[#1a1b1d]' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold w-5 text-center ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-gray-400' : i === 2 ? 'text-orange-400' : sub}`}>#{i + 1}</span>
                          <div>
                            <p className={`text-xs font-medium ${txt}`}>{String(((op as unknown as Record<string, unknown>).operator as Record<string, unknown>)?.name || `${((op as unknown as Record<string, unknown>).operator as Record<string, unknown>)?.firstName || ''} ${((op as unknown as Record<string, unknown>).operator as Record<string, unknown>)?.lastName || ''}`.trim() || (op as unknown as Record<string, unknown>).operatorName || (op as unknown as Record<string, unknown>).name || 'Operator')}</p>
                            <p className={`text-xs ${sub}`}>{t('admin.payoutPage.leaderboard.score', { defaultValue: 'Score' })}: {String((op as unknown as Record<string, unknown>).rewardScore ?? (op as unknown as Record<string, unknown>).score ?? 0)}</p>
                          </div>
                        </div>
                        <span className="text-xs font-bold text-blue-400">{Number((op as unknown as Record<string, unknown>).finalRewardKXT ?? (op as unknown as Record<string, unknown>).kxtReward ?? (op as unknown as Record<string, unknown>).reward ?? 0).toFixed(2)} KXT</span>
                      </div>
                    ))}
                  </div>
            }
          </div>
        </div>

        {/* Withdrawals Table */}
        <div className={card}>
          <div className={`p-4 sm:p-5 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <h2 className={`text-lg font-bold ${txt} mb-4`}>{t('admin.payoutPage.withdrawals.title', { defaultValue: 'KXT Withdrawal Requests' })}</h2>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${sub}`} />
                <input type="text" placeholder={t('admin.payoutPage.withdrawals.searchPlaceholder', { defaultValue: 'Search by ID, operator or method...' })}
                  value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  className={`w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-[#1a1b1d] border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'}`} />
              </div>
              <div className="relative">
                <button onClick={() => setIsStatusOpen(p => !p)}
                  className={`px-4 py-2 border rounded-lg flex items-center gap-2 text-sm font-medium ${isDark ? 'border-gray-600 bg-[#1a1b1d] text-white' : 'border-gray-300 bg-white text-gray-700'}`}>
                  {statusFilter} <ChevronLeft className="w-4 h-4 rotate-[-90deg]" />
                </button>
                {isStatusOpen && (
                  <div className={`absolute mt-2 w-36 rounded-lg shadow-lg z-20 border ${isDark ? 'bg-[#1a1b1d] border-gray-600' : 'bg-white border-gray-200'}`}>
                    {[
                      t('admin.common.all', { defaultValue: 'All' }),
                      t('admin.common.pending', { defaultValue: 'Pending' }),
                      t('admin.payoutPage.status.processing', { defaultValue: 'Processing' }),
                      t('admin.payoutPage.status.completed', { defaultValue: 'Completed' }),
                      t('admin.payoutPage.status.rejected', { defaultValue: 'Rejected' }),
                    ].map(s => (
                      <button key={s} onClick={() => { setStatusFilter(s); setIsStatusOpen(false); fetchWithdrawals(1, s); }}
                        className={`w-full text-left px-3 py-2 text-sm ${isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'}`}>{s}</button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => setIsFilterOpen(true)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium ${isDark ? 'bg-white text-black' : 'bg-black text-white'}`}>
                <Filter className="w-4 h-4" /> {t('admin.payoutPage.withdrawals.filter', { defaultValue: 'Filter' })}
              </button>
            </div>
          </div>

          {/* Date Modal */}
          {isFilterOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/60" onClick={() => setIsFilterOpen(false)} />
              <div className={`relative w-[92%] max-w-md rounded-lg p-6 shadow-xl ${isDark ? 'bg-[#1a1b1d] border border-gray-700' : 'bg-white border border-gray-200'}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-lg font-semibold ${txt}`}>{t('admin.payoutPage.filterByDate.title', { defaultValue: 'Filter by Date' })}</h3>
                  <button onClick={() => setIsFilterOpen(false)} className={`text-sm px-2 py-1 rounded ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}>{t('admin.payoutPage.common.close', { defaultValue: 'Close' })}</button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={`block text-sm mb-1 ${sub}`}>{t('admin.payoutPage.filterByDate.start', { defaultValue: 'Start Date' })}</label><input type="date" max={todayDate} value={startDate} onChange={e => setStartDate(e.target.value)} className={inp} /></div>
                  <div><label className={`block text-sm mb-1 ${sub}`}>{t('admin.payoutPage.filterByDate.end', { defaultValue: 'End Date' })}</label><input type="date" max={todayDate} value={endDate} onChange={e => setEndDate(e.target.value)} className={inp} /></div>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <button onClick={() => { setStartDate(''); setEndDate(''); }} className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-900'}`}>{t('admin.payoutPage.common.clear', { defaultValue: 'Clear' })}</button>
                  <button onClick={() => { fetchWithdrawals(1); setIsFilterOpen(false); }} className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-white text-black' : 'bg-black text-white'}`}>{t('admin.payoutPage.common.apply', { defaultValue: 'Apply' })}</button>
                </div>
              </div>
            </div>
          )}

          {/* Reject Modal */}
          {showRejectModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/60" onClick={() => { setShowRejectModal(false); setRejectReason(''); setSelectedW(null); }} />
              <div className={`relative w-[92%] max-w-md rounded-lg p-6 shadow-xl ${isDark ? 'bg-[#1a1b1d] border border-gray-700' : 'bg-white border border-gray-200'}`}>
                <h3 className={`text-lg font-semibold mb-3 ${txt}`}>{t('admin.payoutPage.reject.title', { defaultValue: 'Reject Withdrawal' })}</h3>
                <p className={`text-sm mb-3 ${sub}`}>ID: <span className="font-mono font-bold">{shortId(selectedW?._id)}</span> - Amount: <span className="font-bold">{fmtAmount(selectedW?.amount, getWithdrawalCurrency(selectedW))}</span></p>
                <label className={`block text-sm mb-1 ${sub}`}>{t('admin.payoutPage.reject.reason', { defaultValue: 'Reason (optional)' })}</label>
                <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} placeholder={t('admin.payoutPage.reject.reasonPlaceholder', { defaultValue: 'Enter rejection reason...' })} className={`${inp} resize-none`} />
                <div className="mt-4 flex justify-end gap-2">
                  <button onClick={() => { setShowRejectModal(false); setRejectReason(''); setSelectedW(null); }} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-900'}`}>{t('admin.payoutPage.common.cancel', { defaultValue: 'Cancel' })}</button>
                  <button onClick={handleRejectConfirm} disabled={!!actionLoading} className="px-4 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} {t('admin.payoutPage.reject.button', { defaultValue: 'Reject' })}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Details Modal */}
          {showDetailsModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/60" onClick={closeDetailsModal} />
              <div className={`relative z-10 flex w-full max-w-5xl flex-col overflow-hidden rounded-2xl border shadow-2xl ${isDark ? 'border-gray-700 bg-[#1a1b1d]' : 'border-gray-200 bg-white'}`}>
                <div className={`flex items-start justify-between gap-4 border-b px-5 py-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div>
                    <h3 className={`text-lg font-semibold ${txt}`}>{t('admin.payoutPage.details.title', { defaultValue: 'Withdrawal Details' })}</h3>
                    <p className={`mt-1 text-sm ${sub}`}>
                      {detailsWithdrawal?._id ? shortId(detailsWithdrawal._id) : shortId(detailsRequestId || undefined)}
                      {detailsWithdrawal?.status ? ` - ${fmtMethod(detailsWithdrawal.status)}` : ''}
                    </p>
                  </div>
                  <button
                    onClick={closeDetailsModal}
                    className={`rounded-lg p-2 transition ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
                    aria-label={t('admin.payoutPage.common.close', { defaultValue: 'Close' })}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="max-h-[80vh] overflow-y-auto p-5 sm:p-6">
                  {detailsLoading && (
                    <div className="flex min-h-[240px] items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                    </div>
                  )}

                  {!detailsLoading && detailsError && (
                    <div className={`rounded-xl border px-4 py-6 text-sm ${isDark ? 'border-red-900 bg-red-950/40 text-red-300' : 'border-red-200 bg-red-50 text-red-700'}`}>
                      {detailsError}
                    </div>
                  )}

                  {!detailsLoading && !detailsError && !detailsWithdrawal && (
                    <div className={`rounded-xl border px-4 py-6 text-sm ${isDark ? 'border-gray-700 bg-[#0f1011] text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                      {t('admin.payoutPage.details.empty.generic', { defaultValue: 'No details were returned for this withdrawal.' })}
                    </div>
                  )}

                  {!detailsLoading && !detailsError && detailsWithdrawal && (
                    <div className="space-y-4">
                      <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
                        <InfoSection title={t('admin.payoutPage.details.sections.operator', { defaultValue: 'Operator' })}>
                          <div className="flex items-center gap-3">
                            {detailsUser?.profileImage ? (
                              <img
                                src={detailsUser.profileImage}
                                alt={detailsUserName || detailsUser?.email || 'Operator'}
                                className="h-16 w-16 rounded-full object-cover"
                              />
                            ) : (
                              <div className={`flex h-16 w-16 items-center justify-center rounded-full text-lg font-semibold ${isDark ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-700'}`}>
                                {detailsUserInitial || 'U'}
                              </div>
                            )}
                            <div>
                              <p className={`text-base font-semibold ${txt}`}>{detailsUserName || 'Unknown Operator'}</p>
                              <p className={`text-sm ${sub}`}>{detailsUser?.email || 'No email available'}</p>
                            </div>
                          </div>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <InfoField label={t('admin.payoutPage.table.operator', { defaultValue: 'Operator' })} value={detailsUserName} />
                            <InfoField label={t('admin.payoutPage.details.sections.role', { defaultValue: 'Role' })} value={fmtMethod(detailsUser?.role || '')} />
                            <InfoField label={t('admin.payoutPage.details.sections.email', { defaultValue: 'Email' })} value={detailsUser?.email} />
                            <InfoField label={t('admin.payoutPage.details.sections.phone', { defaultValue: 'Phone' })} value={detailsUser?.phone} />
                            <InfoField label={t('admin.payoutPage.details.sections.userId', { defaultValue: 'User ID' })} value={detailsUser?.id} mono />
                          </div>
                        </InfoSection>

                        <InfoSection title={t('admin.payoutPage.details.sections.overview', { defaultValue: 'Overview' })}>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <InfoField label={t('admin.payoutPage.table.amount', { defaultValue: 'Amount' })} value={fmtKxtWithUsd(detailsWithdrawal.amount, detailsWithdrawal.currency || 'KXT')} />
                            <InfoField label={t('admin.payoutPage.details.sections.netAmount', { defaultValue: 'Net Amount' })} value={fmtKxtWithUsd(detailsWithdrawal.netAmount, detailsWithdrawal.currency || 'KXT')} />
                            <InfoField label={t('admin.payoutPage.details.sections.fee', { defaultValue: 'Fee' })} value={fmtKxtWithUsd(detailsWithdrawal.fee, detailsWithdrawal.currency || 'KXT')} />
                            <InfoField label={t('admin.payoutPage.table.method', { defaultValue: 'Method' })} value={fmtMethod(detailsWithdrawal.withdrawalMethod || '')} />
                            <InfoField label={t('admin.payoutPage.table.status', { defaultValue: 'Status' })} value={fmtMethod(detailsWithdrawal.status || '')} />
                            <InfoField label={t('admin.payoutPage.details.sections.currency', { defaultValue: 'Currency' })} value={detailsWithdrawal.currency} />
                            <InfoField label={t('admin.payoutPage.details.sections.requestedAt', { defaultValue: 'Requested At' })} value={fmtDateTime(detailsWithdrawal.metadata?.requestedAt)} />
                            <InfoField label={t('admin.payoutPage.details.sections.createdAt', { defaultValue: 'Created At' })} value={fmtDateTime(detailsWithdrawal.createdAt)} />
                            <InfoField label={t('admin.payoutPage.details.sections.updatedAt', { defaultValue: 'Updated At' })} value={fmtDateTime(detailsWithdrawal.updatedAt)} />
                            <InfoField label={t('admin.payoutPage.details.sections.processedBy', { defaultValue: 'Processed By' })} value={detailsWithdrawal.processedBy} mono />
                          </div>
                        </InfoSection>
                      </div>

                      <div className="grid gap-4 lg:grid-cols-2">
                        <InfoSection title={t('admin.payoutPage.details.sections.withdrawalDestination', { defaultValue: 'Withdrawal Destination' })}>
                          {withdrawalDetailsEntries.length > 0 ? (
                            <div className="grid gap-3 sm:grid-cols-2">
                              {withdrawalDetailsEntries.map(([key, value]) => (
                                <InfoField key={key} label={humanizeKey(key)} value={value} mono={isMonospaceValue(key)} />
                              ))}
                            </div>
                          ) : (
                            <p className={`text-sm ${sub}`}>{t('admin.payoutPage.details.empty.destination', { defaultValue: 'No withdrawal destination details were returned.' })}</p>
                          )}
                        </InfoSection>

                        <InfoSection title={t('admin.payoutPage.details.sections.wallets', { defaultValue: 'Wallet Context' })}>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <InfoField label={t('admin.payoutPage.details.sections.operatorWallet', { defaultValue: 'Operator Wallet' })} value={withdrawalDetails?.operatorWallet} mono />
                            <InfoField label={t('admin.payoutPage.details.sections.operatorKXTBalance', { defaultValue: 'Operator KXT Balance' })} value={fmtKxtWithUsd(withdrawalDetails?.operatorKXTBalance, 'KXT')} />
                            {walletDetailsEntries.map(([key, value]) => (
                              <InfoField key={key} label={humanizeKey(key)} value={value} mono={isMonospaceValue(key)} />
                            ))}
                          </div>
                        </InfoSection>
                      </div>

                      <InfoSection title={t('admin.payoutPage.details.sections.metadata', { defaultValue: 'Metadata' })} className={metadataEntries.length > 0 ? '' : 'opacity-80'}>
                        {metadataEntries.length > 0 ? (
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {metadataEntries.map(([key, value]) => (
                              <InfoField key={key} label={humanizeKey(key)} value={(key.toLowerCase().includes('at') || key.toLowerCase().includes('date')) && typeof value === 'string' && isValidDate(value) ? fmtDateTime(value) : value} mono={isMonospaceValue(key)} />
                            ))}
                          </div>
                        ) : (
                          <p className={`text-sm ${sub}`}>{t('admin.payoutPage.details.empty.metadata', { defaultValue: 'No extra metadata was returned for this withdrawal.' })}</p>
                        )}
                      </InfoSection>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Mint Modal */}
          {showMintModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/60" onClick={() => { setShowMintModal(false); setMintOperatorSearch(''); setIsMintOperatorOpen(false); }} />
              <div className={`relative w-[92%] max-w-md rounded-lg p-6 shadow-xl ${isDark ? 'bg-[#1a1b1d] border border-gray-700' : 'bg-white border border-gray-200'}`}>
                <h3 className={`text-lg font-semibold mb-4 ${txt}`}>{t('admin.payoutPage.mint.title', { defaultValue: 'Mint KXT to Operator' })}</h3>
                <div className="space-y-3">
                  <div className="relative">
                    <label className={`block text-sm mb-1 ${sub}`}>{t('admin.payoutPage.mint.selectOperator', { defaultValue: 'Select Operator' })}</label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setIsMintOperatorOpen(!isMintOperatorOpen)}
                        className={`flex w-full items-center justify-between ${inp} ${!mintForm.userId && 'text-gray-500'}`}
                      >
                        <span className="truncate">
                          {mintForm.userId
                            ? String((operators.find(op => (op as unknown as Record<string, unknown>).operatorId === mintForm.userId) as unknown as Record<string, unknown>)?.operatorLabel) || t('admin.payoutPage.mint.selectOperatorPlaceholder', { defaultValue: '-- Select Operator --' })
                            : t('admin.payoutPage.mint.selectOperatorPlaceholder', { defaultValue: '-- Select Operator --' })}
                        </span>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </button>
                      
                      {isMintOperatorOpen && (
                        <div className={`absolute z-50 mt-1 w-full rounded-md border shadow-lg ${isDark ? 'bg-[#1a1b1d] border-gray-600' : 'bg-white border-gray-200'}`}>
                          <div className={`flex items-center border-b px-3 ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
                            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                            <input
                              className={`flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-gray-500 disabled:cursor-not-allowed disabled:opacity-50 ${isDark ? 'text-white' : 'text-black'}`}
                              placeholder={t('admin.payoutPage.mint.searchPlaceholder', { defaultValue: 'Search by name or email...' })}
                              value={mintOperatorSearch}
                              onChange={(e) => setMintOperatorSearch(e.target.value)}
                            />
                            {operatorsLoading && <Loader2 className="h-4 w-4 shrink-0 animate-spin opacity-50" />}
                          </div>
                          <div className="max-h-60 overflow-y-auto p-1">
                            {operatorsLoading && operators.length === 0 ? (
                              <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-blue-500" /></div>
                            ) : operators.length === 0 ? (
                              <div className="py-6 text-center text-sm text-gray-500">{t('admin.payoutPage.mint.noOperatorFound', { defaultValue: 'No operator found.' })}</div>
                            ) : (
                              operators.map(op => (
                                  <div
                                    key={String((op as unknown as Record<string, unknown>).operatorId)}
                                    className={`relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:cursor-pointer ${isDark ? 'hover:bg-gray-700 text-white' : 'hover:bg-gray-100 text-black'} ${mintForm.userId === String((op as unknown as Record<string, unknown>).operatorId) ? (isDark ? 'bg-gray-700' : 'bg-gray-100') : ''}`}
                                    onClick={() => {
                                      setMintForm(f => ({ ...f, userId: String((op as unknown as Record<string, unknown>).operatorId) }));
                                      setIsMintOperatorOpen(false);
                                      setMintOperatorSearch('');
                                    }}
                                  >
                                    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                      {mintForm.userId === String((op as unknown as Record<string, unknown>).operatorId) && <Check className="h-4 w-4" />}
                                    </span>
                                    <span className="truncate">{String((op as unknown as Record<string, unknown>).operatorLabel)}</span>
                                  </div>
                                ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm mb-1 ${sub}`}>{t('admin.payoutPage.mint.amount', { defaultValue: 'Amount (KXT)' })}</label>
                    <input type="number" min="1" placeholder={t('admin.payoutPage.mint.amountPlaceholder', { defaultValue: 'e.g. 100' })} value={mintForm.amount} onChange={e => setMintForm(f => ({ ...f, amount: e.target.value }))} className={inp} />
                    {activeKxtUsdRate > 0 && Number(mintForm.amount) > 0 && (
                      <p className={`mt-1 text-xs ${sub}`}>{t('admin.payoutPage.common.approx', { defaultValue: 'Approx.' })} {fmtUsdFromKxt(Number(mintForm.amount))}</p>
                    )}
                  </div>
                  <div>
                    <label className={`block text-sm mb-1 ${sub}`}>{t('admin.payoutPage.mint.reason', { defaultValue: 'Reason (optional)' })}</label>
                    <input type="text" placeholder={t('admin.payoutPage.mint.reasonPlaceholder', { defaultValue: 'e.g. Bonus reward' })} value={mintForm.reason} onChange={e => setMintForm(f => ({ ...f, reason: e.target.value }))} className={inp} />
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                  <button onClick={() => { setShowMintModal(false); setMintOperatorSearch(''); setIsMintOperatorOpen(false); }} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-900'}`}>{t('admin.payoutPage.common.cancel', { defaultValue: 'Cancel' })}</button>
                  <button onClick={handleMint} disabled={mintLoading} className="px-4 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                    {mintLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} {t('admin.payoutPage.mint.button', { defaultValue: 'Mint KXT' })}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className={`${isDark ? 'bg-[#1a1b1d] border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
                <tr>
                  {[
                    t('admin.payoutPage.table.withdrawalId', { defaultValue: 'Withdrawal ID' }),
                    t('admin.payoutPage.table.operator', { defaultValue: 'Operator' }),
                    t('admin.payoutPage.table.amount', { defaultValue: 'Amount' }),
                    t('admin.payoutPage.table.method', { defaultValue: 'Method' }),
                    t('admin.payoutPage.table.date', { defaultValue: 'Date' }),
                    t('admin.payoutPage.table.status', { defaultValue: 'Status' }),
                    t('admin.payoutPage.table.actions', { defaultValue: 'Actions' }),
                  ].map(h => (
                    <th key={h} className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${sub}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'bg-[#333436] divide-gray-700' : 'bg-white divide-gray-200'}`}>
                {wLoading && <tr><td colSpan={7} className={`px-4 py-8 text-center ${sub}`}><Loader2 className="w-6 h-6 animate-spin mx-auto" /></td></tr>}
                {!wLoading && filtered.length === 0 && <tr><td colSpan={7} className={`px-4 py-8 text-center ${sub}`}>{t('admin.payoutPage.withdrawals.empty', { defaultValue: 'No withdrawal requests found.' })}</td></tr>}
                {!wLoading && filtered.map(w => {
                  const rowCurrency = getWithdrawalCurrency(w);

                  return (
                    <tr key={w._id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                      <td className="px-4 py-4 whitespace-nowrap"><span className={`text-xs font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>#{w._id?.slice(-8).toUpperCase()}</span></td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <p className={`text-sm font-medium ${txt}`}>{w.user?.firstName} {w.user?.lastName}</p>
                        <p className={`text-xs ${sub}`}>{w.user?.email}</p>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <p className={`text-sm font-bold ${txt}`}>{fmtAmount(w.amount, rowCurrency)}</p>
                        {rowCurrency === 'KXT' && activeKxtUsdRate > 0 && <p className={`text-xs ${sub}`}>{t('admin.payoutPage.common.approx', { defaultValue: 'Approx.' })} {fmtUsdFromKxt(w.amount)}</p>}
                        {w.fee != null && w.fee > 0 && <p className={`text-xs ${sub}`}>{t('admin.payoutPage.common.fee', { defaultValue: 'Fee:' })} {fmtKxtWithUsd(w.fee, rowCurrency)}</p>}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{fmtMethod(w.withdrawalMethod)}</p>
                        {w.withdrawalDetails?.walletAddress && <p className={`text-xs font-mono ${sub}`}>{w.withdrawalDetails.walletAddress.slice(0, 10)}...</p>}
                        {w.withdrawalDetails?.bankName && <p className={`text-xs ${sub}`}>{w.withdrawalDetails.bankName}</p>}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap"><span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{fmtDate(w.createdAt)}</span></td>
                      <td className="px-4 py-4 whitespace-nowrap"><span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${statusBadge(w.status)}`}>{w.status}</span></td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {w.status?.toLowerCase() === 'pending' && (
                            <>
                              <button onClick={() => handleApprove(w)} disabled={!!actionLoading} className="p-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-50">
                                {actionLoading === w._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                              </button>
                              <button onClick={() => { setSelectedW(w); setShowRejectModal(true); }} disabled={!!actionLoading} className="p-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">
                                <XCircle className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleViewWithdrawal(w._id)}
                           
                            disabled={detailsLoading && detailsRequestId === w._id}
                            className={`p-1.5 rounded-lg disabled:opacity-50 ${isDark ? 'text-gray-400 hover:bg-gray-600' : 'text-gray-500 hover:bg-gray-100'}`}
                          >
                            {detailsLoading && detailsRequestId === w._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className={`px-4 py-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} flex flex-col sm:flex-row items-center justify-between gap-3`}>
            <span className={`text-sm ${sub}`}>
              {pagination.totalItems === 0 ? t('admin.payoutPage.common.zeroOfZero', { defaultValue: '0 of 0' }) : t('admin.payoutPage.common.paginationRange', { defaultValue: '{{start}} to {{end}} of {{total}}', start: (pagination.currentPage - 1) * pagination.itemsPerPage + 1, end: Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems), total: pagination.totalItems })}
            </span>
            <div className="flex items-center gap-2">
              <button onClick={() => fetchWithdrawals(pagination.currentPage - 1)} disabled={!pagination.hasPrevious} className={`p-2 border rounded-lg disabled:opacity-50 ${isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`}>
                <ChevronLeft className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
              {getPagination(pagination.currentPage, pagination.totalPages).map((page, idx) => (
                <button key={`${page}-${idx}`} disabled={page === '...'} onClick={() => typeof page === 'number' && fetchWithdrawals(page)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium border ${page === pagination.currentPage ? 'bg-blue-600 text-white border-blue-600' : isDark ? 'border-gray-600 hover:bg-gray-700 text-gray-300' : 'border-gray-300 hover:bg-gray-50 text-gray-700'} ${page === '...' ? 'cursor-default' : ''}`}>
                  {page}
                </button>
              ))}
              <button onClick={() => fetchWithdrawals(pagination.currentPage + 1)} disabled={!pagination.hasNext} className={`p-2 border rounded-lg disabled:opacity-50 ${isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`}>
                <ChevronRight className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <WithdrawalHistorySection
            title={t('admin.payoutPage.approvedSection.title', { defaultValue: 'Approved Withdrawals' })}
            rows={approvedWithdrawals}
            loading={approvedLoading}
            loadingMore={approvedLoadingMore}
            emptyText={t('admin.payoutPage.approvedSection.empty', { defaultValue: 'No approved withdrawals found.' })}
            icon={<CheckCircle className="h-4 w-4" />}
            accentClass={isDark ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-700'}
            actionType="reject"
            paginationState={approvedPagination}
            onLoadMore={loadMoreApprovedWithdrawals}
            loadMoreLabel={t('admin.payoutPage.approvedSection.loadMore', { defaultValue: 'Load more' })}
            loadingMoreLabel={t('admin.payoutPage.approvedSection.loadingMore', { defaultValue: 'Loading...' })}
            showingLabel={t('admin.payoutPage.approvedSection.showing', {
              defaultValue: 'Showing {{shown}} of {{total}}',
              shown: approvedWithdrawals.length,
              total: approvedPagination.totalItems,
            })}
          />

          <WithdrawalHistorySection
            title={t('admin.payoutPage.rejectedSection.title', { defaultValue: 'Rejected Withdrawals' })}
            rows={rejectedWithdrawals}
            loading={rejectedLoading}
            loadingMore={rejectedLoadingMore}
            emptyText={t('admin.payoutPage.rejectedSection.empty', { defaultValue: 'No rejected withdrawals found.' })}
            icon={<XCircle className="h-4 w-4" />}
            accentClass={isDark ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-700'}
            actionType="approve"
            paginationState={rejectedPagination}
            onLoadMore={loadMoreRejectedWithdrawals}
            loadMoreLabel={t('admin.payoutPage.rejectedSection.loadMore', { defaultValue: 'Load more' })}
            loadingMoreLabel={t('admin.payoutPage.rejectedSection.loadingMore', { defaultValue: 'Loading...' })}
            showingLabel={t('admin.payoutPage.rejectedSection.showing', {
              defaultValue: 'Showing {{shown}} of {{total}}',
              shown: rejectedWithdrawals.length,
              total: rejectedPagination.totalItems,
            })}
          />
        </div>

      </div>
    </div>
  );
};

export default AdminPayoutDashboard;
